import { type SerializeFrom } from '@remix-run/node'
import { Form, Link, NavLink, useLocation, useSubmit } from '@remix-run/react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Container } from '#app/components/layout/container.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { VisuallyHidden } from '#app/components/layout/visually-hidden.tsx'
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.ts'
import { type Links, navigation } from '#app/navigation.ts'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '#app/old_components/context-menu.tsx'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuTrigger } from '#app/old_components/ui/dropdown-menu.tsx'
import { Icon } from '#app/old_components/ui/icon.tsx'
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent } from '#app/old_components/ui/navigation-menu.tsx'
import { type loader as rootLoader, ThemeSwitch } from '#app/root.tsx'
import { cn } from '#app/utils/tailwind-merge.ts'
import { Drawer, DrawerContent, DrawerTrigger } from '../old_components/modals/drawer.tsx'

const prefetch = 'intent'

const logoAsSVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
	<rect width="16" height="16" />
	<g filter="url(#filter0_d_28_3)">
		<path d="M10 6C10 7.10457 9.10457 8 8 8C6.89543 8 6 7.10457 6 6C6 4.89543 6.89543 4 8 4C9.10457 4 10 4.89543 10 6Z" fill="url(#paint0_linear_28_3)" />
	</g>
	<g filter="url(#filter1_d_28_3)">
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M4.14611 7.09306C4.31786 6.9506 4.57258 6.97435 4.71504 7.14611L7.97539 11.077L11.2869 7.14382C11.4306 6.97312 11.6855 6.95125 11.8562 7.09497C12.0269 7.23869 12.0488 7.49358 11.905 7.66428L7.97074 12.3372L4.09306 7.66199C3.9506 7.49024 3.97435 7.23552 4.14611 7.09306Z"
			fill="url(#paint1_linear_28_3)"
		/>
	</g>
	<g filter="url(#filter2_d_28_3)">
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M13.0162 4.68033L8.15242 1.87223C8.0581 1.81777 7.9419 1.81777 7.84758 1.87223L2.9838 4.68033C2.88949 4.73478 2.83138 4.83542 2.83138 4.94433V10.5605C2.83138 10.6694 2.88949 10.7701 2.9838 10.8245L7.84758 13.6326C7.9419 13.6871 8.0581 13.6871 8.15242 13.6326L13.0162 10.8245C13.1105 10.7701 13.1686 10.6694 13.1686 10.5605V4.94433C13.1686 4.83542 13.1105 4.73478 13.0162 4.68033ZM8.56811 1.15223C8.21656 0.949258 7.78344 0.949258 7.43189 1.15223L2.56811 3.96033C2.21656 4.16329 2 4.53839 2 4.94433V10.5605C2 10.9665 2.21656 11.3416 2.56811 11.5445L7.43189 14.3526C7.78344 14.5556 8.21656 14.5556 8.56811 14.3526L13.4319 11.5445C13.7834 11.3416 14 10.9665 14 10.5605V4.94433C14 4.53839 13.7834 4.16329 13.4319 3.96033L8.56811 1.15223Z"
			fill="url(#paint2_linear_28_3)"
		/>
	</g>
	<defs>
		<filter id="filter0_d_28_3" x="5.8" y="3.8" width="4.4" height="4.4" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
			<feFlood floodOpacity="0" result="BackgroundImageFix" />
			<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
			<feOffset />
			<feGaussianBlur stdDeviation="0.1" />
			<feComposite in2="hardAlpha" operator="out" />
			<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
			<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_28_3" />
			<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_28_3" result="shape" />
		</filter>
		<filter id="filter1_d_28_3" x="3.8" y="6.8" width="8.4" height="5.73716" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
			<feFlood floodOpacity="0" result="BackgroundImageFix" />
			<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
			<feOffset />
			<feGaussianBlur stdDeviation="0.1" />
			<feComposite in2="hardAlpha" operator="out" />
			<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
			<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_28_3" />
			<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_28_3" result="shape" />
		</filter>
		<filter id="filter2_d_28_3" x="1.8" y="0.8" width="12.4" height="13.9049" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
			<feFlood floodOpacity="0" result="BackgroundImageFix" />
			<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
			<feOffset />
			<feGaussianBlur stdDeviation="0.1" />
			<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
			<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_28_3" />
			<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_28_3" result="shape" />
		</filter>
		<linearGradient id="paint0_linear_28_3" x1="8" y1="3.55556" x2="8" y2="8.48148" gradientUnits="userSpaceOnUse">
			<stop stopColor="#C5F8ED" />
			<stop offset="1" stopColor="#25B18E" />
		</linearGradient>
		<linearGradient id="paint1_linear_28_3" x1="8" y1="7.40405" x2="8" y2="11.7071" gradientUnits="userSpaceOnUse">
			<stop stopColor="#C6F8EE" />
			<stop offset="1" stopColor="#24B18E" />
		</linearGradient>
		<linearGradient id="paint2_linear_28_3" x1="8" y1="0.824225" x2="8" y2="14.6806" gradientUnits="userSpaceOnUse">
			<stop stopColor="#C6F8EE" />
			<stop offset="1" stopColor="#24B18E" />
		</linearGradient>
	</defs>
</svg>`

export default function Header({ data }: { data: SerializeFrom<typeof rootLoader> }) {
	const mainNavigation = navigation.filter(({ name }) => name === 'Getting Started' || name === 'Components')
	const location = useLocation()

	return (
		<Container>
			<header className="h-[--header-height]">
				<Flex justify="between" items="stretch" gap="4" className="py-fluid-6 h-full">
					<DesktopMenu user={data.user} navigation={mainNavigation} />
					{location.pathname !== '/' ?
						<ThemeSwitch id="desktop" userPreference={data.requestInfo.userPrefs.theme} />
					:	null}
				</Flex>
			</header>
		</Container>
	)
}

function DesktopMenu({ user, navigation }: { user: SerializeFrom<typeof rootLoader>['user']; navigation: Links }) {
	const [, copy] = useCopyToClipboard()
	const location = useLocation()

	// The navigation menu hides everything except the logo on less than lg: screens
	return (
		<>
			<NavigationMenu className="w-full" id="radix-ui-hack">
				<NavigationMenuList className="h-full">
					<Flex gap="10" className="h-full">
						<NavigationMenuItem className="mr-auto">
							<ContextMenu>
								<ContextMenuTrigger>
									<NavigationMenuLink asChild>
										<NavLink prefetch={prefetch} to="/" className="-ml-viewport-edge px-viewport-edge block flex h-full items-center rounded-md">
											<Logo />
										</NavLink>
									</NavigationMenuLink>
								</ContextMenuTrigger>
								<ContextMenuContent>
									<ContextMenuItem onClick={() => window.open('https://verveui.pro', '_newtab')} className="border-muted-200 flex gap-3 border-b">
										<Icon name="external-link" />
										<span>Open link in New Tab</span>
									</ContextMenuItem>
									<ContextMenuItem
										onClick={() => {
											copy(logoAsSVG)
											toast.success('Logo copied to clipboard')
										}}
										className="flex gap-3"
									>
										<Icon name="copy" />
										<span>Copy logo as SVG</span>
									</ContextMenuItem>
									<ContextMenuItem asChild className="flex gap-3">
										<Link prefetch={prefetch} to="/brand-kit">
											<Icon name="download" />
											<span>Go to Brand Kit</span>
										</Link>
									</ContextMenuItem>
								</ContextMenuContent>
							</ContextMenu>
						</NavigationMenuItem>

						{/* <NavigationMenuItem>
							<NavigationMenuLink asChild className="text-size-sm hover:bg-muted-2 rounded-md p-10 font-semibold">
								<NavLink
									prefetch={prefetch}
									to="/getting-started/introduction"
									className="after:transition-max-width after:bg-brand-solid relative block after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-1 after:max-w-0 after:duration-500 aria-[current]:after:max-w-full"
								>
									Docs
								</NavLink>
							</NavigationMenuLink>
						</NavigationMenuItem> */}
						{user ?
							<NavigationMenuItem>
								<NavigationMenuLink
									asChild
									className={`text-size-sm hover:bg-muted-2 px-viewport-edge h-full rounded-md font-semibold ${location.pathname === '/' ? '-mr-viewport-edge' : ''}`}
								>
									<NavLink
										prefetch={prefetch}
										to="/profile"
										className="after:transition-max-width after:bg-brand-solid relative flex items-center justify-center after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-1 after:max-w-0 after:duration-500 aria-[current]:after:max-w-full"
									>
										<VisuallyHidden>Account</VisuallyHidden>
										<Icon name="avatar" />
										{user.paid ?
											<span className="bg-muted-300 text-muted-600 ml-1 rounded-md py-0.5 px-1 text-xs">PRO</span>
										:	null}
									</NavLink>
								</NavigationMenuLink>
							</NavigationMenuItem>
						:	null}
					</Flex>
				</NavigationMenuList>
			</NavigationMenu>
		</>
	)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function MobileMenu({ user, navigation }: { user: SerializeFrom<typeof rootLoader>['user']; navigation: Links }) {
	const [open, setOpen] = useState(false)

	return (
		<Drawer open={open} onOpenChange={setOpen}>
			<DrawerTrigger className={cn('min-h-tap min-w-tap box-content flex h-6 w-6 cursor-pointer items-center justify-center rounded-lg p-2.5 lg:hidden')}>
				<Icon name="bars-3" className="min-h-tap min-w-tap h-6 w-6">
					<span className="sr-only">Open main menu</span>
				</Icon>
			</DrawerTrigger>

			<DrawerContent>
				<NavigationMenu orientation="vertical">
					<NavigationMenuList>
						<NavigationMenuItem>
							<NavigationMenuLink asChild>
								<NavLink prefetch={prefetch} to="/" className="inline-flex items-center rounded-md p-3" onClick={() => setOpen(false)}>
									<Logo />
								</NavLink>
							</NavigationMenuLink>
						</NavigationMenuItem>
						{navigation.map(({ name, href }) => (
							<NavigationMenuItem key={name}>
								<NavigationMenuLink asChild>
									<NavLink
										prefetch={prefetch}
										to={href}
										className="after:transition-max-width text-size-sm text-muted-900 after:bg-brand hover:bg-muted-100 relative block rounded-lg py-5 px-10 font-semibold after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-0.5 after:max-w-0 after:duration-500 aria-[current]:after:max-w-full"
										onClick={() => setOpen(false)}
									>
										{name}
									</NavLink>
								</NavigationMenuLink>
							</NavigationMenuItem>
						))}
						{user ?
							<NavigationMenuItem>
								<NavigationMenuTrigger className="after:transition-max-width text-size-sm text-muted-900 after:bg-brand hover:bg-muted-100 relative block rounded-lg py-5 px-10 font-semibold after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-0.5 after:max-w-0 after:duration-500 aria-[current]:after:max-w-full">
									Account{' '}
									{user.paid ?
										<span className="bg-muted-200 text-muted-400 ml-1 rounded-md py-0.5 px-1 text-xs">PRO</span>
									:	null}
								</NavigationMenuTrigger>
								<NavigationMenuContent className="bg-muted-100 rounded-xl px-4">
									<span className="">
										<span className="text-size-xs text-muted-600">
											Logged in as <span className="font-bold">{user.email}</span>
										</span>
									</span>
									<NavigationMenuLink asChild>
										<NavLink
											prefetch={prefetch}
											to="/profile"
											className="text-size-sm text-muted-900 relative block rounded-lg py-2 px-3 font-semibold"
											onClick={() => setOpen(false)}
										>
											Account settings
										</NavLink>
									</NavigationMenuLink>
									<NavigationMenuLink asChild>
										<NavLink
											prefetch={prefetch}
											to="/logout"
											className="text-size-sm text-muted-900 relative block rounded-lg py-2 px-3 font-semibold"
											onClick={() => setOpen(false)}
										>
											Logout
										</NavLink>
									</NavigationMenuLink>
								</NavigationMenuContent>
							</NavigationMenuItem>
						:	<NavigationMenuItem>
								<NavigationMenuLink asChild>
									<NavLink
										prefetch={prefetch}
										to="/login"
										className="after:transition-max-width text-size-sm text-muted-900 after:bg-brand hover:bg-muted-50 relative block rounded-lg py-2 px-3 font-semibold after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-0.5 after:max-w-0 after:duration-500 aria-[current]:after:max-w-full"
										onClick={() => setOpen(false)}
									>
										Login
									</NavLink>
								</NavigationMenuLink>
							</NavigationMenuItem>
						}
						{!user || !user.paid ?
							<NavigationMenuItem>
								<NavigationMenuLink asChild className="text-size-sm hover:bg-muted-100 rounded-md py-3 px-4 font-semibold">
									<NavLink
										prefetch={prefetch}
										to="/get-access"
										className="after:transition-max-width bg-brand text-foreground after:bg-brand hover:bg-brand-bg-hover focus-visible:outline-brand-bg-hover relative block rounded-md py-2.5 px-3.5 text-sm font-semibold shadow-sm after:absolute after:right-0 after:bottom-0 after:left-0 after:block after:h-0.5 after:max-w-0 after:duration-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 aria-[current]:after:max-w-full"
										onClick={() => setOpen(false)}
									>
										Get all-access
									</NavLink>
								</NavigationMenuLink>
							</NavigationMenuItem>
						:	null}
					</NavigationMenuList>
				</NavigationMenu>
			</DrawerContent>
		</Drawer>
	)
}

function Logo() {
	return (
		<Flex justify="between" items="center" gap="6" wrap>
			<svg className="w-fluid-12" viewBox="0 0 1000 1129" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g clipPath="url(#clip0_18_3)">
					<path
						d="M526.5 37.5589L943.764 278.466C959.853 287.755 969.764 304.922 969.764 323.499V805.314C969.764 823.892 959.853 841.058 943.764 850.347L526.5 1091.25C510.411 1100.54 490.589 1100.54 474.5 1091.25L57.2363 850.347C41.1475 841.058 31.2364 823.892 31.2364 805.314V323.499C31.2364 304.922 41.1475 287.755 57.2364 278.466L474.5 37.5588C490.589 28.27 510.411 28.27 526.5 37.5589Z"
						stroke="url(#paint0_linear_18_3)"
						strokeWidth="60"
					/>
					<path d="M195.531 490.615L501.076 858.998L811.233 490.615" stroke="url(#paint1_linear_18_2)" strokeWidth="60" strokeLinecap="round" />
					<g filter="url(#filter0_d_18_3)">
						<circle cx="500.5" cy="429.506" r="124.524" fill="url(#paint2_linear_18_2)" />
					</g>
				</g>
				<defs>
					<filter id="filter0_d_18_3" x="371.976" y="304.982" width="257.048" height="257.048" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
						<feOffset dy="4" />
						<feGaussianBlur stdDeviation="2" />
						<feComposite in2="hardAlpha" operator="out" />
						<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
						<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_18_2" />
						<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_18_2" result="shape" />
					</filter>
					<linearGradient id="paint0_linear_18_3" x1="500.5" y1="-98.5683" x2="500.5" y2="1269.47" gradientUnits="userSpaceOnUse">
						<stop stopColor="#D4FEF6" />
						<stop offset="0.994053" stopColor="#0FA882" />
					</linearGradient>
					<linearGradient id="paint1_linear_18_2" x1="503.382" y1="490.615" x2="503.382" y2="858.998" gradientUnits="userSpaceOnUse">
						<stop stopColor="#C6F8EE" />
						<stop offset="1" stopColor="#24B18E" />
					</linearGradient>
					<linearGradient id="paint2_linear_18_2" x1="500.5" y1="277.31" x2="500.5" y2="584.008" gradientUnits="userSpaceOnUse">
						<stop stopColor="#C5F8ED" />
						<stop offset="1" stopColor="#25B18E" />
					</linearGradient>
					<clipPath id="clip0_18_3">
						<rect width="1000" height="1129" fill="white" />
					</clipPath>
				</defs>
			</svg>
			<span className="[overflow-wrap:anywhere] font-bold text-emerald-500">VerveUI</span>
		</Flex>
	)
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function UserDropdown({ user }: { user: NonNullable<SerializeFrom<typeof rootLoader>['user']> }) {
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="text-size-sm hover:bg-muted-100 ml-auto hidden rounded-md py-3 px-4 font-semibold lg:block">
				<span className="flex gap-2">
					<span>
						Account{' '}
						{user.paid ?
							<span className="bg-muted-200 text-muted-400 ml-1 rounded-md py-0.5 px-1 text-xs">PRO</span>
						:	null}
					</span>
					<Icon name="chevron-down" className="h-4 w-4" />
				</span>
			</DropdownMenuTrigger>

			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem className="border-muted-200 border-b">
						<span className="flex flex-col items-start">
							<span className="text-size-xs text-muted-600">Signed in as</span>
							<span className="font-bold">{user.email}</span>
						</span>
					</DropdownMenuItem>

					<DropdownMenuItem asChild>
						<Link prefetch={prefetch} to="/profile" className="flex gap-3">
							<Icon name="avatar" />
							<span>Account Settings</span>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={event => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef} className="flex gap-3">
							<Icon name="exit" />
							<button type="submit">Logout</button>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
